import type { BuildingType } from '@aztec-realms/shared-types';

import type { ConstructionStageId } from './construction-stage';
import directionalArt from './settlement-directional-art.json' with { type: 'json' };

export interface SettlementBuildingArtRegistration {
  source: string;
  imageSize: readonly [number, number];
  /** Measured alpha >=24 horizontal extent; only used to normalize transparent padding. */
  visibleWidth: number;
  /** Midpoint of the measured alpha >=24 horizontal extent, in source-image pixels. */
  horizontalAlphaCenter: number;
  /** Physical front foundation/pier sample. Only its Y registers ground; X records the sample. */
  groundContact: readonly [number, number];
  contactBasis: string;
  /** Visible mature width relative to the projected occupancy width. */
  visiblePlotRatio: number;
}

/**
 * lake-basin-ground-registration-v3. Coordinates refer to current source PNG pixels.
 * Contacts identify solid base corners or pier feet, not the bottom of a watery alpha box.
 * This registers existing artwork; it does not certify matching ground-plane perspective.
 * Logical dimensions always come from the API. Legacy visual fixtures retain their separately
 * authored footprint fallback and therefore must not be used to prove production size ratios.
 */
export const settlementBuildingArtRegistration = {
  solar_shrine: {
    source: 'solar-shrine-t3-v1.png',
    imageSize: [768, 512],
    visibleWidth: 612,
    horizontalAlphaCenter: 391,
    groundContact: [246, 499],
    contactBasis: 'front stone foundation corner below the stairs',
    visiblePlotRatio: 0.96,
  },
  homes: {
    source: 'home-thatch-v2.png',
    imageSize: [1254, 1254],
    visibleWidth: 1089,
    horizontalAlphaCenter: 631.5,
    groundContact: [595, 1189],
    contactBasis: 'single dwelling front stone foundation corner, alpha inspected',
    visiblePlotRatio: 0.78,
  },
  chinampa_farm: {
    source: 'chinampa-farm-t3-v1.png',
    imageSize: [768, 512],
    visibleWidth: 708,
    horizontalAlphaCenter: 393,
    groundContact: [359, 411],
    contactBasis: 'front retaining-wall post foot; excludes canoe and water',
    visiblePlotRatio: 0.86,
  },
  stone_yard: {
    source: 'stone-yard-v1.png',
    imageSize: [512, 512],
    visibleWidth: 493,
    horizontalAlphaCenter: 256.5,
    groundContact: [289, 436],
    contactBasis: 'front masonry corner footing; excludes shadow',
    visiblePlotRatio: 0.86,
  },
  timber_workshop: {
    source: 'timber-workshop-v1.png',
    imageSize: [512, 512],
    visibleWidth: 490,
    horizontalAlphaCenter: 263,
    groundContact: [302, 460],
    contactBasis: 'front stone platform corner',
    visiblePlotRatio: 0.86,
  },
  obsidian_workshop: {
    source: 'obsidian-workshop-t3-v1.png',
    imageSize: [768, 512],
    visibleWidth: 739,
    horizontalAlphaCenter: 375.5,
    groundContact: [463, 502],
    contactBasis: 'front carved foundation corner beside the stairs',
    visiblePlotRatio: 0.86,
  },
  codex_house: {
    source: 'codex-house-v1.png',
    imageSize: [512, 512],
    visibleWidth: 502,
    horizontalAlphaCenter: 256,
    groundContact: [390, 454],
    contactBasis: 'front archive foundation corner; excludes shadow',
    visiblePlotRatio: 0.86,
  },
  market: {
    source: 'canal-market-dock-t3-v1.png',
    imageSize: [768, 512],
    visibleWidth: 750,
    horizontalAlphaCenter: 385,
    groundContact: [171, 424],
    contactBasis: 'front market pier post foot; excludes boats and water',
    visiblePlotRatio: 0.875,
  },
  dock: {
    source: 'canoe-dock-v1.png',
    imageSize: [512, 512],
    visibleWidth: 466,
    horizontalAlphaCenter: 262,
    groundContact: [280, 410],
    contactBasis: 'front right-pier post foot; excludes canoe tip',
    visiblePlotRatio: 0.86,
  },
  garden: {
    source: 'garden-plaza-v1.png',
    imageSize: [512, 512],
    visibleWidth: 491,
    horizontalAlphaCenter: 262.5,
    groundContact: [341, 452],
    contactBasis: 'front fountain-garden platform corner',
    visiblePlotRatio: 0.85,
  },
} as const satisfies Record<BuildingType, SettlementBuildingArtRegistration>;

/** Renderer inset remains 0.88; normalize alpha width, never inverse logical occupancy. */
function baseBuildingRegistration(
  type: BuildingType,
  level = 1,
  stage: 'completed' | 'structure' = 'completed',
): SettlementBuildingArtRegistration {
  const base = settlementBuildingArtRegistration[type];
  if (type === 'solar_shrine' && stage === 'structure')
    return {
      ...base,
      source: 'solar-shrine-structure-v1.png',
      visibleWidth: 695,
      horizontalAlphaCenter: 385.5,
      groundContact: [385.5, 498],
      contactBasis: 'unfinished shrine front stone footing; stage-specific alpha bounds 38..733',
    };
  if (type === 'solar_shrine' && level >= 4 && level <= 5)
    return {
      ...base,
      source: 'solar-shrine-monument-v2.png',
      imageSize: [1254, 1254],
      visibleWidth: 1113,
      horizontalAlphaCenter: 659.5,
      groundContact: [420, 1202],
      contactBasis: 'front-left solid stone foundation corner; excludes soft alpha fringe',
    };
  if (type !== 'homes' || level < 3) return base;
  return level < 5
    ? {
        ...base,
        source: 'home-terrace-v2.png',
        visibleWidth: 1098,
        horizontalAlphaCenter: 632,
        groundContact: [595, 1192],
      }
    : {
        ...base,
        source: 'home-residence-v2.png',
        visibleWidth: 1098,
        horizontalAlphaCenter: 633,
        groundContact: [596, 1250],
      };
}

/** Each quarter turn is separately rendered; never rotate or reflect upright pixels. */
export function settlementBuildingRegistration(
  type: BuildingType,
  level = 1,
  stage: 'completed' | 'structure' = 'completed',
  orientation = 0,
): SettlementBuildingArtRegistration {
  const base = baseBuildingRegistration(type, level, stage);
  const views = directionalArt as unknown as Record<
    string,
    Record<string, Omit<SettlementBuildingArtRegistration, 'visiblePlotRatio'>>
  >;
  const view = views[base.source]?.[String(orientation)];
  return view ? { ...view, visiblePlotRatio: base.visiblePlotRatio } : base;
}

export function registeredArtworkScale(
  type: BuildingType,
  level = 1,
  stage: 'completed' | 'structure' = 'completed',
  orientation = 0,
): number {
  const art = settlementBuildingRegistration(type, level, stage, orientation);
  // Bound the visible foundation, not the transparent image rectangle. The old
  // image-box cap disproportionately reduced the shrine's padded raster.
  return art.visiblePlotRatio / (0.88 * (art.visibleWidth / art.imageSize[0]));
}

export const settlementBuildingArtManifest = Object.fromEntries(
  (Object.keys(settlementBuildingArtRegistration) as BuildingType[]).map((type) => [
    type,
    registeredArtworkScale(type),
  ]),
) as Record<BuildingType, number>;

/**
 * Plot anchors use the lateral extent midpoint, not the physical front corner's X.
 * Center the visible artwork there while retaining its inspected physical ground-contact Y.
 * Suitable for Phaser setOrigin(x, y), independent of rotation/zoom.
 */
export function settlementBuildingGroundAnchor(
  type: BuildingType,
  stage: 'completed' | 'structure' = 'completed',
  level = 1,
  orientation = 0,
): { x: number; y: number } {
  const art = settlementBuildingRegistration(type, level, stage, orientation);
  return {
    x: art.horizontalAlphaCenter / art.imageSize[0],
    y: art.groundContact[1] / art.imageSize[1],
  };
}

/** Preserve each family's authored tier growth while bounding its mature width. */
export function settlementBuildingTierScale(
  visualTier: number,
  tierScales: readonly [number, number, number, number],
): number {
  return tierScales[Math.max(0, Math.min(3, Math.floor(visualTier)))]! / tierScales[3];
}

/** Finished architecture keeps its final size; earlier ground works occupy the legal plot. */
export function settlementConstructionArtworkWidth(
  stage: ConstructionStageId,
  groundWidth: number,
  completedArtworkWidth: number,
): number {
  return stage === 'finishing' || stage === 'activation' ? completedArtworkWidth : groundWidth;
}

/** Uniform upright rig, visibly separate from the monument while remaining on its plot. */
export function settlementFinishingRig(groundWidth: number): { width: number; offsetX: number } {
  if (!Number.isFinite(groundWidth) || groundWidth <= 0) return { width: 0, offsetX: 0 };
  return { width: groundWidth * 0.46, offsetX: 0 };
}
